import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import type { Paginated } from "@shared/schemas/paginated.schemas";
import {
  HostConnectionRequestsSchema,
  type HostConnectionRequestRow,
} from "@shared/schemas/host-connection-requests.schemas";
import { BaseApiHooksRepository } from "../../base-api-hooks.repository";
import { resolveRequesterAndTargetPhotoUrls } from "@shared/utils/participant-photo-url.utils";
import {
  createConnectionRequestsApi,
  type GetEventConnectionRequestsParams,
} from "./connection-requests.api";

/**
 * Keyed by every parameter the query varies on: a page cached under a key that
 * ignored its filters would serve the wrong rows.
 */
export const CONNECTION_REQUESTS_QUERY_KEYS = {
  byEvent: (params: GetEventConnectionRequestsParams) =>
    [
      "connection-requests",
      "event",
      params.eventIdPublic,
      params.page ?? null,
      params.limit ?? null,
      params.participantIdPublic ?? null,
      params.direction,
      params.vip ?? null,
    ] as const,
} as const;

const CONNECTION_REQUESTS_STALE_TIME_MS = 30_000;

interface GetEventConnectionRequestsOptions {
  readonly enabled?: boolean;
}

// The backend ships the stored `evt-participant-photo://` placeholder verbatim;
// only the client knows the event it must be resolved against.
function resolveRowPhotoUrls(
  response: Paginated<HostConnectionRequestRow>,
  eventIdPublic: EventIdPublic,
): Paginated<HostConnectionRequestRow> {
  return {
    ...response,
    items: resolveRequesterAndTargetPhotoUrls(
      response.items,
      EventIdPublic.unwrap(eventIdPublic),
    ),
  };
}

export class ConnectionRequestsHooksHttpRepositoryImpl extends BaseApiHooksRepository {
  private readonly api;

  constructor(queryClient: QueryClient, apiClient: AxiosInstance) {
    super(queryClient);
    this.api = createConnectionRequestsApi(apiClient);
  }

  createHooks() {
    const { api } = this;
    return {
      useGetEventConnectionRequests: (
        params: GetEventConnectionRequestsParams,
        options: GetEventConnectionRequestsOptions = {},
      ): UseQueryResult<Paginated<HostConnectionRequestRow>> => {
        const { enabled = true } = options;

        return this.query({
          key: CONNECTION_REQUESTS_QUERY_KEYS.byEvent(params),
          fn: () => api.getEventConnectionRequests(params),
          parse: (dto) =>
            resolveRowPhotoUrls(
              HostConnectionRequestsSchema.parse(dto),
              params.eventIdPublic,
            ),
          enabled:
            enabled && EventIdPublic.unwrap(params.eventIdPublic).length > 0,
          staleTime: CONNECTION_REQUESTS_STALE_TIME_MS,
          retry: 1,
          placeholderData: (previousData) => previousData,
        });
      },
    };
  }
}
